import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import Lottie from 'lottie-react';
import { useState } from 'react';
import dataDiscoveryAnimation from '../../assets/animations/data-discovery.json';
import dataComplianceAnimation from '../../assets/animations/data-compliance.json';
import dataDetectionResponseAnimation from '../../assets/animations/data-detection-response.json';
import dataClassificationAnimation from '../../assets/animations/data-classification.json';
import dataGovernanceAnimation from '../../assets/animations/data-governance.json';

const UseCase = ({ title, description, features, benefits, icon: Icon, index }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getSlug = (title) => {
    return title.toLowerCase().replace(/\s+/g, '-');
  };

  // Get the appropriate animation based on the title
  const getAnimation = (title) => {
    if (title === "Data Discovery"){
      return dataDiscoveryAnimation;
    }
    if (title === "Data Classification"){
      return dataClassificationAnimation;
    }
    if (title === "Data Compliance") {
      return dataComplianceAnimation;
    }
    if (title === "Data Detection and Response") {
      return dataDetectionResponseAnimation;
    }
    if (title === "Data Governance") {
      return dataGovernanceAnimation;
    }

    return null;
  };

  const animation = getAnimation(title);

  const renderMedia = () => {
    if (!animation) return null;

    return (
      <div 
        className="flex-shrink-0 w-48 h-48 cursor-pointer hover:opacity-80 transition-opacity"
        onClick={() => setIsModalOpen(true)}
      >
        <Lottie
          animationData={animation}
          loop={true}
          autoplay={true}
          className="w-full h-full"
        />
      </div>
    );
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className="flex flex-col lg:flex-row items-start gap-6 p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
      >
        <div className="flex-shrink-0">
          <div className="w-16 h-16 flex items-center justify-center rounded-lg bg-blue-50">
            <Icon className="w-8 h-8 text-ancile-blue" />
          </div>
        </div>
        <div className="flex-grow space-y-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">{title}</h3>
            <p className="text-gray-600 leading-relaxed">{description}</p>
          </div>
          
          {features && (
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-800">Key Features</h4>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {benefits && (
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-2">Benefits</h4>
              <p className="text-gray-600">{benefits}</p>
            </div>
          )}
        </div>
        {renderMedia()}
      </motion.div>

      {/* Modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg p-4 max-w-4xl w-full mx-4 relative"
            onClick={e => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              onClick={() => setIsModalOpen(false)}
            >
              ✕
            </button>
            <div className="w-full h-[600px]">
              <Lottie
                animationData={animation}
                loop={true}
                autoplay={true}
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UseCase; 